import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { WorkoutView, WorkoutExercise, WorkoutHistory, ExerciseDefinition, WorkoutTemplateData, MovementUnit, MOVEMENT_UNITS } from '../../types';
import { seoConfig } from '../../utils/seoConfig';
import StructuredData from '../seo/StructuredData';
import TodayWorkout from './today/TodayWorkout';
import WorkoutHistoryComponent from './history/WorkoutHistory';
import HomeWorkoutTemplates from './templates/HomeWorkoutTemplates';
import EditWorkoutExercise from './today/EditWorkoutExercise';
import { WorkoutCompletionModal } from './today/WorkoutCompletionModal';
import { WorkoutSavingOverlay } from './today/WorkoutSavingOverlay';
import { apiServices } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/provider';
import PageShell from '../layout/PageShell';
import styles from './WorkoutPage.module.css';

interface WorkoutPageProps {
  view: WorkoutView;
  onOpenTemplates?: () => void;
  onSwitchToToday?: () => void;
  onSwitchToHistory?: () => void;
  onNavigateToExercise?: (exerciseId: string, templateId?: string) => void;
  onNavigateToAddExercise?: () => void;
  selectedTemplateId?: string | null;
  onTemplateSelected?: (templateId: string | null) => void;
}

const WorkoutPage: React.FC<WorkoutPageProps> = ({ view, onOpenTemplates, onSwitchToToday, onSwitchToHistory, onNavigateToExercise, onNavigateToAddExercise, selectedTemplateId, onTemplateSelected }) => {
  // console.log('🏋️ WorkoutPage rendered, view:', view);
  const { user } = useAuth();
  const { t } = useTranslation();
  const ANON_WORKOUT_HISTORY_KEY = 'gym_planner_anonymous_workout_history';
  // console.log('👤 Current user:', `${user?.username}/${user?.id}`);
  const [todayExercises, setTodayExercises] = useState<WorkoutExercise[]>([]);
  const [selectedExercise, setSelectedExercise] = useState<WorkoutExercise | null>(null);
  const [workoutHistory, setWorkoutHistory] = useState<WorkoutHistory[]>([]);
  const [workoutTemplates, setWorkoutTemplates] = useState<WorkoutTemplateData[]>([]);
  // Separate loading states for different data types
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);

  const [selectedWorkoutDate, setSelectedWorkoutDate] = useState<Date>(new Date());
  const [showWorkoutInProgressWarning, setShowWorkoutInProgressWarning] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<WorkoutTemplateData | null>(null);
  const [showWorkoutCompletionModal, setShowWorkoutCompletionModal] = useState(false);
  const [completedWorkoutDate, setCompletedWorkoutDate] = useState<string>('');
  const [isSavingWorkout, setIsSavingWorkout] = useState(false);
  const [workoutSaveError, setWorkoutSaveError] = useState<string>('');

  // Sanitization function to convert string weights to numbers
  const sanitizeExerciseData = (exercises: any[]): WorkoutExercise[] => {
    return exercises.map((ex: any) => ({
      ...ex,
      weight: Number(ex.weight) || 0,
      reps: Number(ex.reps) || 0,
      setsData: ex.setsData?.map((set: any) => ({
        ...set,
        weight: Number(set.weight) || 0,
        reps: Number(set.reps) || 0
      }))
    }));
  };

  // Track workoutHistory state changes
  useEffect(() => {
    console.log('👀 [WORKOUT PAGE] workoutHistory state changed:', {
      workoutCount: workoutHistory.length,
      workoutIds: workoutHistory.map(w => ({ id: w.id, date: w.date })),
      fullState: workoutHistory
    });
  }, [workoutHistory]);

  const WORKOUT_DATE_KEY = 'selected_workout_date';

  const loadAnonymousWorkoutHistory = useCallback((): WorkoutHistory[] => {
    try {
      const raw = localStorage.getItem(ANON_WORKOUT_HISTORY_KEY);
      if (!raw) {
        return [];
      }

      const parsed = JSON.parse(raw) as Array<WorkoutHistory & { date: string }>;
      return parsed.map((workout) => ({
        ...workout,
        date: new Date(workout.date),
      }));
    } catch (error) {
      console.warn('Failed to load anonymous workout history:', error);
      return [];
    }
  }, []);

  const saveAnonymousWorkoutHistory = useCallback((history: WorkoutHistory[]) => {
    try {
      const serialized = history.map((workout) => ({
        ...workout,
        date: workout.date.toISOString(),
      }));
      localStorage.setItem(ANON_WORKOUT_HISTORY_KEY, JSON.stringify(serialized));
    } catch (error) {
      console.warn('Failed to save anonymous workout history:', error);
    }
  }, []);

  // Load saved workout date from localStorage on mount
  useEffect(() => {
    const savedDate = localStorage.getItem(WORKOUT_DATE_KEY);
    if (savedDate) {
      try {
        const date = new Date(savedDate);
        // Only restore if it's within the valid range (not in future, not older than 2 months)
        const now = new Date();
        const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
        
        if (date <= now && date >= twoMonthsAgo) {
          setSelectedWorkoutDate(date);
          console.log('📅 Restored workout date from storage:', date.toISOString().split('T')[0]);
        }
      } catch (error) {
        console.warn('Failed to parse saved workout date:', error);
      }
    }
  }, []);

  // Load different data types in parallel when user is available
  useEffect(() => {
    console.log('useEffect running, user:', user);

    // Load current workout session (today's exercises)
    const loadSession = async () => {
      try {
        console.log('🔄 [WORKOUT PAGE] Loading session data for user:', user?.id || 'anonymous');
        const sessionExercises = await apiServices.workoutSession.getExercises();
        const sanitizedExercises = sanitizeExerciseData(sessionExercises || []);
        
        console.log('🌐 [WORKOUT PAGE] Using fresh session data from backend:', sanitizedExercises.length, 'exercises');

        setTodayExercises(sanitizedExercises);
      } catch (error) {
        console.error('Failed to load workout session:', error);
      } finally {
        setIsLoadingSession(false);
      }
    };
    
    // Load workout history
    const loadHistory = async () => {
      try {
        if (!user) {
          setWorkoutHistory(loadAnonymousWorkoutHistory());
          return;
        }

        console.log('🔄 [WORKOUT PAGE] Loading history for user:', user.id);
        const historyResponse = await apiServices.workoutHistory.getWorkoutHistory(user.id, 1, 8);
        
        console.log('📊 [WORKOUT PAGE] History response received:', {
          totalCount: historyResponse.pagination?.total || 0,
          dataLength: historyResponse.data?.length || 0
        });
        
        setWorkoutHistory(historyResponse.data || []);
      } catch (error) {
        console.error('Failed to load workout history:', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };
    
    // Load templates
    const loadTemplates = async () => {
      try {
        if (!user) {
          // Templates are always user-owned; the backend requires auth and
          // returns 401 for anonymous requests, which triggers a hard redirect
          // (see BaseApiService.redirectToLogin). Skip the call entirely.
          setWorkoutTemplates([]);
          return;
        }

        console.log('🔄 [WORKOUT PAGE] Loading templates');
        const templateData = await apiServices.templates.getTemplates();
        
        const sortedTemplates = (templateData || []).sort((a, b) => {
          const isAOwner = !!user?.id && a.userId === user.id;
          const isBOwner = !!user?.id && b.userId === user.id;
          
          if (isAOwner && !isBOwner) return -1;
          if (!isAOwner && isBOwner) return 1;
          
          return a.name.localeCompare(b.name);
        });
        
        setWorkoutTemplates(sortedTemplates);
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setIsLoadingTemplates(false);
      }
    };
    
    // Start all data loading in parallel
    loadSession();
    loadHistory();
    loadTemplates();
  }, [loadAnonymousWorkoutHistory, user]);

  // Clear selected exercise when view changes
  useEffect(() => {
    setSelectedExercise(null);
  }, [view]);

  const handleAddExerciseToToday = (exerciseDef: ExerciseDefinition) => {
    // Check for duplicates - use ID for accurate duplicate detection
    const isDuplicate = todayExercises.some(ex => ex.id === exerciseDef.id);
    if (isDuplicate) {
      return;
    }

    // TODO.md E, item 5: stretching/mobility exercises collect hold time (seconds, stored in `reps`)
    // instead of reps @ weight - default to a plausible hold length and unit 'sec' rather than the
    // strength default of 8 reps @ 0kg. exerciseType must be carried onto the WorkoutExercise too, not
    // just unit - EditWorkoutExercise/ExerciseInfo key their hold-time UI off it.
    const isHoldTime = exerciseDef.exerciseType === 'stretching' || exerciseDef.exerciseType === 'mobility';

    const newExercise: WorkoutExercise = {
      id: exerciseDef.id, // Use the actual exercise ID from database
      name: exerciseDef.name,
      muscleGroup: exerciseDef.muscleGroup,
      exerciseType: exerciseDef.exerciseType,
      imageUrl: exerciseDef.imageUrl || '',
      sets: 3,
      completedSets: 0,
      reps: isHoldTime ? 30 : 8,
      weight: 0,
      unit: isHoldTime ? 'sec' : 'kg',
      completed: false,
      inProgress: false,
    };
    setTodayExercises(prev => {
      const next = [...prev, newExercise];
      apiServices.workoutSession.setExercises(next);
      return next;
    });
  };

  const handleHistoryExerciseClick = async (exercise: WorkoutHistory['exercises'][number]) => {
    // Look up the exercise definition to get image and muscle group
    const exerciseDef = await apiServices.exercises.findExerciseByName(exercise.name);

    const mapped: WorkoutExercise = {
      id: exerciseDef?.id || `hist-${Date.now()}`, // Use actual exercise ID if found
      name: exercise.name,
      muscleGroup: exerciseDef?.muscleGroup || 'all',
      exerciseType: exerciseDef?.exerciseType,
      imageUrl: exerciseDef?.imageUrl || '',
      sets: exercise.sets.length,
      completedSets: exercise.completed ? exercise.sets.length : 0,
      reps: exercise.sets[0]?.reps || 0,
      weight: exercise.sets[0]?.weight || 0,
      unit: exercise.sets[0]?.unit || 'kg',
      completed: exercise.completed,
      inProgress: false,
    };

    setSelectedExercise(mapped);
    if (onSwitchToToday) {
      onSwitchToToday();
    }
  };

  const handleDeleteExerciseFromToday = (id: string) => {
    setTodayExercises(prev => {
      const next = prev.filter(ex => ex.id !== id);
      apiServices.workoutSession.setExercises(next);
      return next;
    });
  };

  const handleEditExerciseInToday = (exercise: WorkoutExercise) => {
    setSelectedExercise(exercise);
  };

  const handleBackFromDetail = () => {
    setSelectedExercise(null);
  };

  const handleSaveExerciseFromDetail = async (updated: WorkoutExercise) => {
    console.log('💾 [WORKOUT PAGE] Saving exercise from detail:', {
      exerciseName: updated.name,
      exerciseId: updated.id,
      completedSets: updated.completedSets,
      setsData: updated.setsData,
      completed: updated.completed,
      hasSetsData: !!updated.setsData,
      setsDataLength: updated.setsData?.length || 0
    });

    const optimisticNext = todayExercises.map(ex => (ex.id === updated.id ? updated : ex));
    setTodayExercises(optimisticNext);

    // Unmount the edit component to return to the list view
    setSelectedExercise(null);
    
    // Save to backend and WAIT for completion
    try {
      console.log('💾 [WORKOUT PAGE] Waiting for backend save to complete...');
      await apiServices.workoutSession.setExercises(optimisticNext);
      console.log('✅ [WORKOUT PAGE] Backend save completed, now refetching fresh data...');
      
      // Now refetch fresh data from backend to ensure UI shows correct state
      const freshExercises = await apiServices.workoutSession.getExercises();
      
      // Sanitize refetched exercise data - convert string weights to numbers
      const sanitizedFreshExercises = sanitizeExerciseData(freshExercises || []);
      
      console.log('📊 [WORKOUT PAGE] Fresh sanitized exercises from backend:', sanitizedFreshExercises?.map(ex => ({
        name: ex.name,
        id: ex.id,
        completedSets: ex.completedSets,
        setsData: ex.setsData,
        hasSetsData: !!ex.setsData,
        setsDataLength: ex.setsData?.length || 0,
        weight: ex.weight,
        weightType: typeof ex.weight
      })));
      
      if (sanitizedFreshExercises) {
        setTodayExercises(sanitizedFreshExercises);
      }
    } catch (error) {
      console.error('❌ [WORKOUT PAGE] Failed to save exercise:', error);
      // Optionally show error to user or revert state
    }
  };

  const handleTemplateSelect = (template: any) => {
    console.log('Selected template:', template);
  };

  const handleLoadTemplate = async (template: WorkoutTemplateData) => {
    const hasWorkoutInProgress = todayExercises.length > 0;
    
    if (hasWorkoutInProgress) {
      setPendingTemplate(template);
      setShowWorkoutInProgressWarning(true);
      return;
    }

    await loadTemplateExercises(template);
  };

  const loadTemplateExercises = async (template: WorkoutTemplateData) => {
    try {
      // Fetch actual exercise details for each exercise in the template
      const templateExercises: WorkoutExercise[] = await Promise.all(
        template.exercises.map(async (ex, idx) => {
          let exerciseName = `Exercise ${idx + 1}`; // Fallback name
          let muscleGroup = 'unknown' as any;
          let imageUrl = '';
          let exerciseType: ExerciseDefinition['exerciseType'];
          // externalUrl is dead - the backend stopped returning it after migration 15 merged it into
          // image_url (TODO.md B3/F4); imageUrl is the only field populated now.

          try {
            // Try to get exercise details by ID (cached in apiServices.exercises - F2 consolidation)
            const exerciseDef = await apiServices.exercises.getExerciseById(ex.exerciseId);
            if (exerciseDef) {
              exerciseName = exerciseDef.name;
              muscleGroup = exerciseDef.muscleGroup;
              imageUrl = exerciseDef.imageUrl || '';
              // TODO.md E, item 5: needed so EditWorkoutExercise/ExerciseInfo can key their hold-time
              // UI off exerciseType - ex.unit alone (already 'sec' for a hold-time template exercise)
              // is a second, redundant signal those components also check.
              exerciseType = exerciseDef.exerciseType;
            }
          } catch (error) {
            console.warn(`Failed to fetch exercise details for ID ${ex.exerciseId}:`, error);
          }

          return {
            id: ex.exerciseId,
            name: exerciseName,
            muscleGroup,
            exerciseType,
            imageUrl,
            sets: ex.sets,
            completedSets: 0,
            reps: ex.reps,
            weight: ex.weight,
            unit: ex.unit,
            completed: false,
            inProgress: false,
          };
        })
      );

      // Sanitize template exercises - convert string weights to numbers
      const sanitizedTemplateExercises = sanitizeExerciseData(templateExercises);

      setTodayExercises(sanitizedTemplateExercises);
      apiServices.workoutSession.setExercises(sanitizedTemplateExercises);

      if (onSwitchToToday) {
        onSwitchToToday();
      }
    } catch (error) {
      console.error('Failed to load template exercises:', error);
    }
  };

  const handleCloseWarning = () => {
    setShowWorkoutInProgressWarning(false);
    setPendingTemplate(null);
  };

  const handleAddTemplateToWorkout = async () => {
    if (!pendingTemplate) return;

    // Filter out duplicates - check by exerciseId matching with id
    const existingIds = new Set(todayExercises.map(ex => ex.id));
    const newExercises = pendingTemplate.exercises.filter(ex => !existingIds.has(ex.exerciseId));

    try {
      // Fetch actual exercise details for each new exercise
      const templateExercises: WorkoutExercise[] = await Promise.all(
        newExercises.map(async (ex, idx) => {
          let exerciseName = `Exercise ${idx + 1}`; // Fallback name
          let muscleGroup = 'unknown' as any;
          let imageUrl = '';
          let exerciseType: ExerciseDefinition['exerciseType'];
          // externalUrl is dead - the backend stopped returning it after migration 15 merged it into
          // image_url (TODO.md B3/F4); imageUrl is the only field populated now.

          try {
            // Try to get exercise details by ID (cached in apiServices.exercises - F2 consolidation)
            const exerciseDef = await apiServices.exercises.getExerciseById(ex.exerciseId);
            if (exerciseDef) {
              exerciseName = exerciseDef.name;
              muscleGroup = exerciseDef.muscleGroup;
              imageUrl = exerciseDef.imageUrl || '';
              // TODO.md E, item 5: see the identical note in loadTemplateExercises above.
              exerciseType = exerciseDef.exerciseType;
            }
          } catch (error) {
            console.warn(`Failed to fetch exercise details for ID ${ex.exerciseId}:`, error);
          }

          return {
            id: ex.exerciseId,
            name: exerciseName,
            muscleGroup,
            exerciseType,
            imageUrl,
            sets: ex.sets,
            completedSets: 0,
            reps: ex.reps,
            weight: ex.weight,
            unit: ex.unit,
            completed: false,
            inProgress: false,
          };
        })
      );

      // Sanitize additional template exercises - convert string weights to numbers
      const sanitizedAdditionalExercises = sanitizeExerciseData(templateExercises);

      const updatedExercises = [...todayExercises, ...sanitizedAdditionalExercises];
      setTodayExercises(updatedExercises);
      apiServices.workoutSession.setExercises(updatedExercises);

      handleCloseWarning();
      
      // Navigate to today view after adding template exercises
      if (onSwitchToToday) {
        onSwitchToToday();
      }
    } catch (error) {
      console.error('Failed to add template exercises:', error);
    }
  };

const handleEndWorkout = async (exs: WorkoutExercise[]) => {
  if (!exs || exs.length === 0) {
    return;
  }

  const formatDate = (d: Date) => d.toISOString().split('T')[0];
  const today = new Date();
  const isSameDate = (a: Date, b: Date) => formatDate(a) === formatDate(b);

  if (!isSameDate(selectedWorkoutDate, today)) {
    const ok = window.confirm(
      t('workout.today.dateConfirm', {
        selectedDate: formatDate(selectedWorkoutDate),
        todayDate: formatDate(today),
      })
    );
    if (!ok) {
      return;
    }
  }

  console.log('💪 Ending workout for date:', selectedWorkoutDate.toISOString().split('T')[0]);
  
  // Start loading state
  setIsSavingWorkout(true);

  // Clean up exercise IDs before saving
  const cleanedExercises = await Promise.all(exs.map(async (ex) => {
    console.log('🔍 Processing exercise:', ex.name, 'with ID:', ex.id);
    
    // Try to find exercise by current ID to verify it exists
    try {
      await apiServices.exercises.getExerciseById(ex.id);
      console.log('✅ Exercise ID is valid:', ex.id);
      return ex; // ID is valid, keep it
    } catch (idError) {
      console.log('❌ Exercise ID not found, trying by name:', ex.id);
      
      // ID is invalid, get proper ID by name
      try {
        const exerciseDef = await apiServices.exercises.findExerciseByName(ex.name);
        if (exerciseDef?.id) {
          console.log('✅ Found exercise by name, new ID:', exerciseDef.id);
          return {
            ...ex,
            id: exerciseDef.id
          };
        } else {
          throw new Error('Exercise not found by name');
        }
      } catch (nameError) {
        console.error('❌ Failed to find exercise by name:', ex.name, nameError);
        
        // Last resort: try to get all exercises and find a match
        try {
          const allExercises = await apiServices.exercises.getExercises();
          const matchedExercise = allExercises.find(e => 
            e.name.toLowerCase() === ex.name.toLowerCase() ||
            e.name.toLowerCase().includes(ex.name.toLowerCase()) ||
            ex.name.toLowerCase().includes(e.name.toLowerCase())
          );
          
          if (matchedExercise) {
            console.log('✅ Found exercise by fuzzy match:', matchedExercise.id);
            return {
              ...ex,
              id: matchedExercise.id
            };
          }
        } catch (fallbackError) {
          console.error('❌ Fallback search also failed:', fallbackError);
        }
        
        // If all else fails, keep the original ID but log the error
        console.warn('⚠️ Keeping original ID for exercise:', ex.name, ex.id);
        return ex;
      }
    }
  }));

  try {
    // Debug: Check the cleaned exercises data before transformation
    console.log('🔍 [WORKOUT] Cleaned exercises before transformation:', cleanedExercises.map(ex => ({
      name: ex.name,
      completed: ex.completed,
      completedSets: ex.completedSets,
      sets: ex.sets,
      setsType: typeof ex.sets,
      isArray: Array.isArray(ex.sets),
      reps: ex.reps,
      weight: ex.weight
    })));

    // Transform WorkoutExercise to WorkoutHistory.exercises format
    const transformedExercises = cleanedExercises.map(ex => {
      console.log('🔄 [WORKOUT] Transforming exercise:', ex.name, {
        setsValue: ex.sets,
        setsType: typeof ex.sets,
        isArray: Array.isArray(ex.sets),
        completedSets: ex.completedSets
      });
      
      let exerciseSets: Array<{ reps: number; weight: number; unit: MovementUnit }> = [];

      // TODO.md E, item 5: 'sec' must survive this fallback the same way 'kg'/'lb'/'bw'/'assist'
      // always did, or a hold-time set silently reverts to 'kg' the moment it takes cases 3/4 below
      // (e.g. saving a workout without ever going through setsData).
      const normalizeUnit = (unit: unknown): MovementUnit =>
        (MOVEMENT_UNITS as readonly string[]).includes(unit as string) ? (unit as MovementUnit) : 'kg';

      // Handle different sets data formats
      if (ex.setsData && Array.isArray(ex.setsData) && ex.setsData.length > 0) {
        // Case 1: setsData is available - use completed sets array
        exerciseSets = ex.setsData;
        console.log('✅ [WORKOUT] Using setsData array for', ex.name);
      } else if (Array.isArray(ex.sets) && ex.sets.length > 0) {
        // Case 2: sets is already an array of set objects
        exerciseSets = ex.sets;
        console.log('✅ [WORKOUT] Using existing sets array for', ex.name);
      } else if (typeof ex.sets === 'number' && ex.sets > 0) {
        // Case 3: sets is a number - create array of set objects
        exerciseSets = Array.from({ length: ex.sets }, (_, index) => ({
          reps: ex.reps || 10,
          weight: ex.weight || 0,
          unit: normalizeUnit(ex.unit)
        }));
        console.log('🔧 [WORKOUT] Created sets array from number for', ex.name, ':', exerciseSets);
      } else if (ex.completedSets && ex.completedSets > 0) {
        // Case 4: use completedSets count as fallback
        exerciseSets = Array.from({ length: ex.completedSets }, (_, index) => ({
          reps: ex.reps || 10,
          weight: ex.weight || 0,
          unit: normalizeUnit(ex.unit)
        }));
        console.log('🔧 [WORKOUT] Created sets array from completedSets for', ex.name, ':', exerciseSets);
      } else {
        // Case 5: no sets data - empty array (will be caught by validation)
        console.log('⚠️ [WORKOUT] No sets data found for', ex.name, '- using empty array');
      }

      console.log('✅ [WORKOUT] Final exerciseSets for', ex.name, ':', exerciseSets);

      return {
        id: ex.id,
        name: ex.name,
        completed: ex.completed,
        sets: exerciseSets
      };
    });

    // Debug: Check the transformed exercises data
    console.log('🔄 [WORKOUT] Transformed exercises for saving:', transformedExercises.map(ex => ({
      name: ex.name,
      completed: ex.completed,
      setsCount: ex.sets?.length || 0,
      firstSet: ex.sets?.[0] || null
    })));

    console.log('🔄 Prepared workout data with', transformedExercises.length, 'exercises');

    // Create workout data with selected date and required fields
    const workoutData = {
      exercises: transformedExercises,
      date: selectedWorkoutDate.toISOString().split('T')[0], // Format: YYYY-MM-DD
      userId: user?.id || 'user_demo_1',
      exerciseCount: transformedExercises.length,
      duration: '0 min' // Default duration, can be calculated later if needed
    };

    if (!user) {
      const localWorkout: WorkoutHistory = {
        id: `anon-${Date.now()}`,
        userId: 'anonymous',
        date: new Date(selectedWorkoutDate),
        exerciseCount: transformedExercises.length,
        duration: workoutData.duration,
        exercises: transformedExercises,
      };

      const updatedHistory = [localWorkout, ...loadAnonymousWorkoutHistory()].sort(
        (a, b) => b.date.getTime() - a.date.getTime()
      );

      saveAnonymousWorkoutHistory(updatedHistory);
      setWorkoutHistory(updatedHistory);

      const today = new Date();
      setSelectedWorkoutDate(today);
      localStorage.setItem(WORKOUT_DATE_KEY, today.toISOString());

      setTodayExercises(() => {
        const next: WorkoutExercise[] = [];
        void apiServices.workoutSession.setExercises(next);
        return next;
      });

      setCompletedWorkoutDate(workoutData.date);
      setShowWorkoutCompletionModal(true);
      setWorkoutSaveError('');
      setIsSavingWorkout(false);
      return;
    }

    console.log('💾 Saving workout data:', workoutData);
    await apiServices.workoutHistory.saveWorkout(workoutData);
    console.log('✅ Workout saved successfully for date:', workoutData.date);
    
    // Clear workout history cache to ensure fresh data
    console.log('🧹 Clearing workout history cache after save...');
    apiServices.workoutHistory.clearAllCachesForUser(user?.id || 'user_demo_1');
    
    // Reset date to today after successful workout completion
    const today = new Date();
    setSelectedWorkoutDate(today);
    localStorage.setItem(WORKOUT_DATE_KEY, today.toISOString());
    console.log('📅 Reset workout date to today after completion:', today.toISOString().split('T')[0]);
    
    // Small delay to ensure backend has processed the workout
    console.log('⏳ Waiting for backend to process workout...');
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Reload history (first page)
    console.log('🔄 [WORKOUT PAGE] Refreshing workout history after save for user:', user?.id || 'user_demo_1');
    const historyResponse = await apiServices.workoutHistory.getWorkoutHistory(user?.id || 'user_demo_1', 1, 8);
    console.log('📊 [WORKOUT PAGE] History refresh response received:', {
      total: historyResponse.pagination.total,
      page: historyResponse.pagination.page,
      dataLength: historyResponse.data?.length,
      workoutIds: historyResponse.data?.map(w => ({ id: w.id, date: w.date, exerciseCount: w.exerciseCount })) || [],
      firstWorkoutDetails: historyResponse.data?.[0] || null,
      fullResponse: historyResponse
    });
    
    console.log('🔄 [WORKOUT PAGE] About to update workoutHistory state after save with:', historyResponse.data);
    setWorkoutHistory(historyResponse.data);
    console.log('✅ [WORKOUT PAGE] workoutHistory state updated after save');
    
    // Clear today's workout
    setTodayExercises(() => {
      const next: WorkoutExercise[] = [];
      apiServices.workoutSession.setExercises(next);
      return next;
    });

    // Show workout completion modal
    setCompletedWorkoutDate(workoutData.date);
    setShowWorkoutCompletionModal(true);
    
    console.log('🎉 Workout completed and modal shown for date:', workoutData.date);
    
    // Clear any previous error and stop loading on success
    setWorkoutSaveError('');
    setIsSavingWorkout(false);
  } catch (error: any) {
    console.error('❌ Error saving workout:', error);
    
    // Set error state for the overlay - don't auto-close
    setWorkoutSaveError(error.message || 'Failed to save workout');
  }
  };

  const handleWorkoutErrorAcknowledge = () => {
    setIsSavingWorkout(false);
    setWorkoutSaveError('');
  };

  const handleWorkoutCompletionModalClose = () => {
    setShowWorkoutCompletionModal(false);
    
    // Switch to history view to show the completed workout
    if (onSwitchToHistory) {
      onSwitchToHistory();
    }
    
    console.log('🔚 Workout completion modal closed, switching to history');
  };

  const renderContent = () => {
    // Show loading state for the current view if data is still loading
    if (view === 'today' && isLoadingSession) {
      return (
        <div className={styles.fullPageLoadingContainer}>
          <div className={styles.fullPageLoadingSpinner}></div>
          <p className={styles.fullPageLoadingText}>{t('workout.loading.today')}</p>
        </div>
      );
    }

    if (view === 'history' && isLoadingHistory) {
      return (
        <div className={styles.fullPageLoadingContainer}>
          <div className={styles.fullPageLoadingSpinner}></div>
          <p className={styles.fullPageLoadingText}>{t('workout.loading.history')}</p>
        </div>
      );
    }

    if (view === 'templates' && isLoadingTemplates) {
      return (
        <div className={styles.fullPageLoadingContainer}>
          <div className={styles.fullPageLoadingSpinner}></div>
          <p className={styles.fullPageLoadingText}>{t('workout.loading.templates')}</p>
        </div>
      );
    }
    
    switch (view) {
      case 'today':
        return selectedExercise ? (
          <EditWorkoutExercise
            exercise={selectedExercise}
            onBack={handleBackFromDetail}
            onSave={handleSaveExerciseFromDetail}
            onNavigateToExercise={onNavigateToExercise}
          />
        ) : (
          <TodayWorkout
              exercises={todayExercises}
              onAddExercise={handleAddExerciseToToday}
              onDeleteExercise={handleDeleteExerciseFromToday}
              onEditExercise={handleEditExerciseInToday}
              onEndWorkout={handleEndWorkout}
              onOpenTemplates={onOpenTemplates}
              onNavigateToAddExercise={onNavigateToAddExercise}
              selectedDate={selectedWorkoutDate}
              onDateChange={(date) => {
                console.log('📅 Workout date changed to:', date.toISOString().split('T')[0]);
                setSelectedWorkoutDate(date);
                // Save to localStorage for persistence
                localStorage.setItem(WORKOUT_DATE_KEY, date.toISOString());
                console.log('💾 Saved workout date to storage:', date.toISOString().split('T')[0]);
              }}
            />
        );
      case 'history':
        console.log('🎬 [WORKOUT PAGE] Rendering history view with workouts:', {
          workoutCount: workoutHistory.length,
          workoutIds: workoutHistory.map(w => ({ id: w.id, date: w.date })),
          initialWorkouts: workoutHistory
        });
        return (
          <WorkoutHistoryComponent 
            initialWorkouts={workoutHistory} 
            onExerciseClick={handleHistoryExerciseClick} 
            onNavigateToExercise={onNavigateToExercise}
            data-testid="workout-list"
          />
        );
      case 'templates':
        return <HomeWorkoutTemplates 
          templates={workoutTemplates} 
          onSelectTemplate={handleTemplateSelect} 
          onLoadTemplate={handleLoadTemplate}
          onNavigateToExercise={onNavigateToExercise}
          selectedTemplateId={selectedTemplateId}
          onTemplateSelected={onTemplateSelected}
          onTemplatesRefresh={async () => {
            console.log('🔄 Refreshing templates...');
            const templateData = await apiServices.templates.getTemplates();
            
            // Sort templates: owned templates first, then public templates
            const sortedTemplates = (templateData || []).sort((a, b) => {
              // Owned templates (where userId matches current user) come first
              const isAOwner = a.userId === user?.id;
              const isBOwner = b.userId === user?.id;
              
              if (isAOwner && !isBOwner) return -1;
              if (!isAOwner && isBOwner) return 1;
              
              // If both have same ownership status, sort by name
              return a.name.localeCompare(b.name);
            });
            
            setWorkoutTemplates(sortedTemplates);
          }}
        />;
      default:
        return <div>Unknown view</div>;
    }
  };

  const getSEOConfig = () => {
    const baseConfig = seoConfig.workouts;
    
    if (view === 'today') {
      return {
        ...baseConfig,
        title: "Today's Workout - Daily Exercise Planner | Gym Planner",
        description: "Plan and track today's workout routine. Log exercises, sets, reps, and weights. Stay consistent with your fitness goals."
      };
    } else if (view === 'history') {
      return {
        ...baseConfig,
        title: 'Workout History - Past Exercise Records | Gym Planner',
        description: 'View your complete workout history and track fitness progress over time. Analyze past workouts and improve your training.'
      };
    } else if (view === 'templates') {
      return {
        ...baseConfig,
        title: 'Workout Templates - Pre-made Exercise Routines | Gym Planner',
        description: 'Browse and use workout templates for different fitness goals. Save time with proven exercise routines for all levels.'
      };
    }
    
    return baseConfig;
  };

  const seoData = getSEOConfig();

  return (
    <>
      <StructuredData type="WebApplication" />
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        {seoData.keywords && <meta name="keywords" content={seoData.keywords} />}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <PageShell variant="page">
        {!showWorkoutInProgressWarning ? (
          renderContent()
        ) : (
          pendingTemplate && (
            <div className={styles.modalOverlay} onClick={handleCloseWarning}>
              <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <h3>Workout Already Started</h3>
                <p>You have a workout in progress. You can either close this dialog or add the template exercises to your current workout.</p>
                
                <div className={styles.templatePreview}>
                  <h4>{pendingTemplate.localizedName || pendingTemplate.name}</h4>
                  <ul className={styles.exerciseList}>
                    {pendingTemplate.exercises.map((ex, idx) => (
                      <li key={idx}>
                        {/* TODO.md E, item 5: this dialog isn't localized elsewhere (hardcoded English
                            strings above), so the hold-time branch stays plain text too rather than
                            introducing t() for just this one line. */}
                        {ex.unit === 'sec'
                          ? `Exercise ${idx + 1} - ${ex.sets} sets × ${ex.reps}s hold`
                          : `Exercise ${idx + 1} - ${ex.sets} sets × ${ex.reps} reps @ ${ex.weight} ${ex.unit}`}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={styles.modalActions}>
                  <button className="button button--secondary" onClick={handleCloseWarning}>
                    Cancel
                  </button>
                  <button className="button button--primary" onClick={handleAddTemplateToWorkout}>
                    Add to Workout
                  </button>
                </div>
              </div>
            </div>
          )
        )}

        <WorkoutCompletionModal
          isOpen={showWorkoutCompletionModal}
          onClose={handleWorkoutCompletionModalClose}
          workoutDate={completedWorkoutDate}
        />

        <WorkoutSavingOverlay
          isVisible={isSavingWorkout}
          message="Saving your workout..."
          hasError={!!workoutSaveError}
          errorMessage={workoutSaveError}
          onErrorAcknowledge={handleWorkoutErrorAcknowledge}
        />
      </PageShell>
    </>
  );
};

export default WorkoutPage;